import api, { ApiResponse } from './api';

// User types
export interface User {
  id: number;
  name: string;
  email: string;
  role: 'super_admin' | 'tenant_admin' | 'drive_manager' | 'interviewer';
  tenant_id?: number;
  phone?: string;
  avatar?: string;
  is_active: boolean;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  company_name: string;
  plan: 'starter' | 'professional' | 'enterprise';
}

export interface AuthResponse {
  user: User;
  token: string;
  token_type: string;
}

export interface SocialLoginResponse {
  url: string;
}

// Authentication Service
class AuthService {
  // Login user
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<ApiResponse<AuthResponse>>('/login', credentials);
    const { user, token } = response.data.data;
    
    // Store token and user data
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user', JSON.stringify(user));
    
    return response.data.data;
  }

  // Register user
  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post<ApiResponse<AuthResponse>>('/register', data);
    const { user, token } = response.data.data;
    
    // Store token and user data
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user', JSON.stringify(user));
    
    return response.data.data;
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      await api.post('/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('user_plan');
    }
  }

  // Get current user
  async getCurrentUser(): Promise<User> {
    const response = await api.get<ApiResponse<{ user: User; user_plan: any }>>('/user');
    const { user, user_plan } = response.data.data;
    
    // Update stored user data
    localStorage.setItem('user', JSON.stringify(user));
    if (user_plan) {
      localStorage.setItem('user_plan', JSON.stringify(user_plan));
    }
    
    return user;
  }

  // Refresh token
  async refreshToken(): Promise<AuthResponse> {
    const response = await api.post<ApiResponse<AuthResponse>>('/refresh');
    const { user, token } = response.data.data;
    
    // Update stored token and user data
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user', JSON.stringify(user));
    
    return response.data.data;
  }

  // Change password
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/change-password', {
      current_password: currentPassword,
      password: newPassword,
      password_confirmation: newPassword,
    });
  }

  // Forgot password
  async forgotPassword(email: string): Promise<void> {
    await api.post('/forgot-password', { email });
  }

  // Reset password
  async resetPassword(token: string, password: string, passwordConfirmation: string): Promise<void> {
    await api.post('/reset-password', {
      token,
      password,
      password_confirmation: passwordConfirmation,
    });
  }

  // Social login redirect
  async socialLoginRedirect(provider: 'google' | 'linkedin'): Promise<string> {
    const response = await api.get<SocialLoginResponse>(`/auth/${provider}/redirect`);
    return response.data.url;
  }

  // Social login callback
  async socialLoginCallback(provider: 'google' | 'linkedin', code: string): Promise<AuthResponse> {
    const response = await api.get<ApiResponse<AuthResponse>>(`/auth/${provider}/callback`, {
      params: { code }
    });
    const { user, token } = response.data.data;
    
    // Store token and user data
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user', JSON.stringify(user));
    
    return response.data.data;
  }

  // Get stored user
  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.error('Error parsing stored user:', error);
      return null;
    }
  }

  // Get stored plan data
  getStoredPlan(): any | null {
    const planStr = localStorage.getItem('user_plan');
    if (!planStr) return null;
    
    try {
      return JSON.parse(planStr);
    } catch (error) {
      console.error('Error parsing stored plan:', error);
      return null;
    }
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  }

  // Check if user has specific role
  hasRole(role: string): boolean {
    const user = this.getStoredUser();
    return user?.role === role;
  }

  // Check if user has any of the specified roles
  hasAnyRole(roles: string[]): boolean {
    const user = this.getStoredUser();
    return user ? roles.includes(user.role) : false;
  }

  // Check if user is super admin
  isSuperAdmin(): boolean {
    return this.hasRole('super_admin');
  }

  // Check if user is tenant admin
  isTenantAdmin(): boolean {
    return this.hasRole('tenant_admin');
  }

  // Check if user is drive manager
  isDriveManager(): boolean {
    return this.hasRole('drive_manager');
  }

  // Check if user is interviewer
  isInterviewer(): boolean {
    return this.hasRole('interviewer');
  }
}

export default new AuthService();
